import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {batch, useSelector, useDispatch} from 'react-redux';
import PipettingProfileWrapper from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileWrapper';
import PipettingProfileItems from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileItem/PipettingProfileItems';
import {PipettingParameterFieldKey, TaskType} from 'constants/dataEnum';
import {getDefaultTipPosition} from 'stores/selectors/configSelectors';
import {
  setTaskMixingPositionAspirate,
  setTaskMixingPositionDispense,
  setTaskTipPosition,
  setTaskEnableMixing,
  setTaskMixingCycle,
  setTaskMixingVolume,
  setTaskSpeed,
  setTaskRetractSpeed,
  setTaskMixingSpeed,
  setTaskPrecisionDispense,
  setTaskTipTouchOff,
  setTaskTipTouchOffDelay,
  setTaskTipTouchOffDirectionX,
  setTaskTipTouchOffDirectionY,
  setTaskTipTouchOffHeight,
} from 'stores/actions/cardStateAction';
import PipettingProfileExpanderButton from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileExpanderButton';
import {getMinTipPositionByLabwareId, updateRunningProtocol} from 'stores/business/protocolManagementBusiness';
import {globalAddUndoItem} from 'stores/business/undoRedoBusinessAction';
import {updatePipettingTipPositionOffset} from 'stores/business/pipettingParameterBusiness';

function SerialDilutionTransferPipettingProfile({tasks, runtimeMode}) {
  const dispatch = useDispatch();
  const [isPipettingProfileExpanded, setIsPipettingProfileExpanded] = useState(false);

  const defaultTipPosition = useSelector(getDefaultTipPosition);

  const {[TaskType.ASPIRATE]: aspirateTasks, [TaskType.DISPENSE]: dispenseTasks} = tasks || [];

  const sampleAspirateTask = aspirateTasks && aspirateTasks[0];
  const sampleDispenseTask = dispenseTasks && dispenseTasks[0];

  // serial dilution process can be done across different plates with different heights,
  // so the tip position should be adjusted accordingly
  const adjustTipPosition = (newValue, pipettingKey, taskType) => {
    const affectedTasks = tasks[taskType];
    const sampleTask = tasks[taskType][0];

    const diffPosition = newValue - (sampleTask[pipettingKey] || 0);
    const minTipPositionSampleTask = dispatch(getMinTipPositionByLabwareId(sampleTask.labwareID));
    const newSampleValue = parseFloat(
      (Math.ceil(((sampleTask[pipettingKey] || 0) + diffPosition) * 100) / 100).toFixed(2),
    );

    const taskIdsAndAdjustedValue = {};

    affectedTasks.forEach((task) => {
      const minTipPosition = dispatch(getMinTipPositionByLabwareId(task.labwareID));

      let adjustedValue = parseFloat(
        (Math.ceil((((task[pipettingKey] || 0) + diffPosition) * 100).toFixed(2)) / 100).toFixed(2),
      );

      adjustedValue =
        minTipPositionSampleTask <= defaultTipPosition &&
        minTipPosition > defaultTipPosition &&
        (adjustedValue <= minTipPosition || newSampleValue <= defaultTipPosition)
          ? minTipPosition
          : adjustedValue;

      taskIdsAndAdjustedValue[task.ID] = adjustedValue;
    });

    return taskIdsAndAdjustedValue;
  };

  const postUpdateAction = () => {
    if (runtimeMode) {
      dispatch(updateRunningProtocol());
    } else {
      dispatch(globalAddUndoItem());
    }
  };

  const getAffectedTaskIdsAndUpdateValue = (updateFunction, value, taskType) => {
    const affectedTasks = taskType ? tasks[taskType] : [...tasks[TaskType.ASPIRATE], ...tasks[TaskType.DISPENSE]];

    batch(() => {
      affectedTasks.forEach((task) => dispatch(updateFunction(task.ID, value)));

      postUpdateAction();
    });
  };

  const updatePipettingTipPositionValue = (direction, value, taskType) => {
    const affectedTasks = taskType ? tasks[taskType] : [...tasks[TaskType.ASPIRATE], ...tasks[TaskType.DISPENSE]];

    batch(() => {
      affectedTasks.forEach((task, index) => {
        dispatch(updatePipettingTipPositionOffset(direction, value, task, index !== 0));
      });
      postUpdateAction();
    });
  };

  const setTipPosition = (value, taskType) => getAffectedTaskIdsAndUpdateValue(setTaskTipPosition, value, taskType);
  const setTipXPosition = (value, taskType) => updatePipettingTipPositionValue('x', value, taskType);
  const setTipYPosition = (value, taskType) => updatePipettingTipPositionValue('y', value, taskType);
  const setSpeed = (value, taskType) => getAffectedTaskIdsAndUpdateValue(setTaskSpeed, value, taskType);
  const setRetractSpeed = (value, taskType) => getAffectedTaskIdsAndUpdateValue(setTaskRetractSpeed, value, taskType);
  const setEnableMixing = (value, taskType) => getAffectedTaskIdsAndUpdateValue(setTaskEnableMixing, value, taskType);
  const setMixingVolume = (value, taskType) => getAffectedTaskIdsAndUpdateValue(setTaskMixingVolume, value, taskType);
  const setMixingCycle = (value, taskType) => getAffectedTaskIdsAndUpdateValue(setTaskMixingCycle, value, taskType);
  const setMixingSpeed = (value, taskType) => getAffectedTaskIdsAndUpdateValue(setTaskMixingSpeed, value, taskType);
  const setTipTouchOffDelay = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOffDelay, value, taskType);
  const setEnableTipTouchOff = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOff, value, taskType);

  const setTipTouchOffHeight = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOffHeight, value, taskType);
  const setTipTouchOffDirectionX = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOffDirectionX, value, taskType);
  const setTipTouchOffDirectionY = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOffDirectionY, value, taskType);

  const setPrecisionDispense = (value) =>
    getAffectedTaskIdsAndUpdateValue(setTaskPrecisionDispense, value, TaskType.DISPENSE);

  const setMixingPosition = async (value, taskType, updateFunction) => {
    const isChangeBothMixingPosition = !updateFunction;

    if (isChangeBothMixingPosition) {
      batch(async () => {
        await setMixingPosition(value, taskType, setTaskMixingPositionAspirate);
        await setMixingPosition(value, taskType, setTaskMixingPositionDispense);
      });

      return;
    }

    const adjustedPositionValue = adjustTipPosition(value, PipettingParameterFieldKey.MIXING_POSITION, taskType);

    batch(() => {
      Object.entries(adjustedPositionValue).forEach((idWithValue) => {
        const [idInString, adjustedValue] = idWithValue;
        const id = Number(idInString);

        dispatch(updateFunction(id, adjustedValue));
      });

      postUpdateAction();
    });
  };

  const setMixingPositionAspirate = (value, taskType) => {
    setMixingPosition(value, taskType, setTaskMixingPositionAspirate);
  };

  const setMixingPositionDispense = (value, taskType) => {
    setMixingPosition(value, taskType, setTaskMixingPositionDispense);
  };

  return (
    <div>
      {isPipettingProfileExpanded && (
        <PipettingProfileWrapper>
          <PipettingProfileItems.TipPosition
            task={sampleAspirateTask}
            onChange={setTipPosition}
            onXTipPositionOnChange={setTipXPosition}
            onYTipPositionOnChange={setTipYPosition}
          />
          <PipettingProfileItems.Speed task={sampleAspirateTask} onChange={setSpeed} />
          <PipettingProfileItems.RetractSpeed task={sampleAspirateTask} onChange={setRetractSpeed} />
          <PipettingProfileItems.EnableMixing task={sampleAspirateTask} onChange={setEnableMixing} />
          <PipettingProfileItems.MixingDetailGroup
            task={sampleAspirateTask}
            onChangeMixingPosition={setMixingPosition}
            onChangeMixingPositionAspirate={setMixingPositionAspirate}
            onChangeMixingPositionDispense={setMixingPositionDispense}
            onChangeCycle={setMixingCycle}
            onChangeVolume={setMixingVolume}
            onChangeMixingSpeed={setMixingSpeed}
          />
          <PipettingProfileItems.TipTouchOff
            task={sampleAspirateTask}
            onChangeSwitch={setEnableTipTouchOff}
            onChangeDelay={setTipTouchOffDelay}
            onChangeHeight={setTipTouchOffHeight}
            onChangeDirectionX={setTipTouchOffDirectionX}
            onChangeDirectionY={setTipTouchOffDirectionY}
          />
          <PipettingProfileItems.TipPosition
            task={sampleDispenseTask}
            onChange={setTipPosition}
            onXTipPositionOnChange={setTipXPosition}
            onYTipPositionOnChange={setTipYPosition}
          />
          <PipettingProfileItems.PrecisionDispense task={sampleDispenseTask} onChange={setPrecisionDispense} />
          <PipettingProfileItems.Speed task={sampleDispenseTask} onChange={setSpeed} />
          <PipettingProfileItems.RetractSpeed task={sampleDispenseTask} onChange={setRetractSpeed} />
          <PipettingProfileItems.EnableMixing task={sampleDispenseTask} onChange={setEnableMixing} />
          <PipettingProfileItems.MixingDetailGroup
            task={sampleDispenseTask}
            onChangeMixingPosition={setMixingPosition}
            onChangeMixingPositionAspirate={setMixingPositionAspirate}
            onChangeMixingPositionDispense={setMixingPositionDispense}
            onChangeCycle={setMixingCycle}
            onChangeVolume={setMixingVolume}
            onChangeMixingSpeed={setMixingSpeed}
          />
          <PipettingProfileItems.TipTouchOff
            task={sampleDispenseTask}
            onChangeSwitch={setEnableTipTouchOff}
            onChangeDelay={setTipTouchOffDelay}
            onChangeHeight={setTipTouchOffHeight}
            onChangeDirectionX={setTipTouchOffDirectionX}
            onChangeDirectionY={setTipTouchOffDirectionY}
          />
        </PipettingProfileWrapper>
      )}
      <PipettingProfileExpanderButton isExpanded={isPipettingProfileExpanded} onClick={setIsPipettingProfileExpanded} />
    </div>
  );
}

SerialDilutionTransferPipettingProfile.propTypes = {
  tasks: PropTypes.object.isRequired,
  runtimeMode: PropTypes.bool,
};

SerialDilutionTransferPipettingProfile.defaultProps = {
  runtimeMode: false,
};

export default SerialDilutionTransferPipettingProfile;
